import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ClientOrderCreateRequest,
  ClientOrderUpdateRequest,
  ResponseClientInstruments,
  ResponseClientOrder,
  ResponseClientOrderKV,
  ResponseClientRates,
} from '../../domain/entities/ClientOrdersEntity';
import { clientOrderKeys, clientOrderRepository } from '../../infrastructure/ClientOrdersConstants';

// Hook para obtener todas las órdenes de clientes
export const useGetClientOrders = () => {
  return useQuery<ResponseClientOrderKV, Error>({
    queryKey: clientOrderKeys.all,
    queryFn: () => clientOrderRepository.getAllClientOrders(),
  });
}

// Hook para obtener una orden de cliente por ID
export const useGetClientOrderById = (id: string) => {
  return useQuery<ResponseClientOrderKV, Error>({
    queryKey: clientOrderKeys.details(id),
    queryFn: () => clientOrderRepository.getClientOrderById(id),
    enabled: !!id, // Solo se ejecuta si hay un ID
  });
}

// Hook para crear una orden de cliente
export const useCreateClientOrder = () => {
  const queryClient = useQueryClient();

  return useMutation<ResponseClientOrder, Error, ClientOrderCreateRequest>({
    mutationFn: (order) => clientOrderRepository.createClientOrder(order),
    onSuccess: () => {
      // Invalida la caché de todas las órdenes para forzar una recarga
      queryClient.invalidateQueries({ queryKey: clientOrderKeys.all });
    },
  });
}

// Hook para actualizar una orden de cliente
export const useUpdateClientOrder = () => {
  const queryClient = useQueryClient();

  return useMutation<ResponseClientOrder, Error, ClientOrderUpdateRequest>({
    mutationFn: (order) => clientOrderRepository.updateClientOrder(order),
    onSuccess: (_, variables) => {
      // Invalida tanto la lista completa como los detalles de la orden específica
      queryClient.invalidateQueries({ queryKey: clientOrderKeys.all });
      if (variables.id) {
        queryClient.invalidateQueries({ queryKey: clientOrderKeys.details(variables.id) });
      }
    },
  });
}

// Hook para eliminar una orden de cliente
export const useDeleteClientOrder = () => {
  const queryClient = useQueryClient();

  return useMutation<ResponseClientOrder, Error, string>({
    mutationFn: (id) => clientOrderRepository.deleteClientOrder(id),
    onSuccess: (_, id) => {
      // Invalida la caché de todas las órdenes
      queryClient.invalidateQueries({ queryKey: clientOrderKeys.all });
      // Elimina la caché de la orden específica
      queryClient.removeQueries({ queryKey: clientOrderKeys.details(id) });
    },
  });
}

export const useGetClientInstruments = () => {
  return useQuery<ResponseClientInstruments, Error>({
    queryKey: clientOrderKeys.allInstruments,
    queryFn: () => clientOrderRepository.getAllClientInstruments(),
  });
}

export const useGetClientRates = () => {
  return useQuery<ResponseClientRates, Error>({
    queryKey: clientOrderKeys.allRates,
    queryFn: () => clientOrderRepository.getAllClientRates(),
  });
}
